import {
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChevronRight,
  IconSparkles,
  IconUser,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import type { RefreshInterval } from '../api/queries';
import { useWishes } from '../api/queries';
import type { Wish } from '../api/types';
import { formatMoney } from '../lib/format';
import { WishDetails } from './WishDetails';

/** The whole list is shown at once; beyond this it is cut off with a hint. */
export const WISHLIST_LIMIT = 50;

const SKELETON_ROWS = 4;

/**
 * Wanted purchases, most wanted first. Read-only like the rest of the UI:
 * wishes are created and edited outside this viewing-only interface.
 */
export function WishlistPanel({
  refreshInterval,
  onCountChange,
}: {
  refreshInterval: RefreshInterval;
  onCountChange?: (count: number | null) => void;
}) {
  const { data, isPending, error, refetch } = useWishes(WISHLIST_LIMIT, refreshInterval);
  const [selectedWishId, setSelectedWishId] = useState<string | null>(null);

  const wishes = data?.wishes ?? [];

  useEffect(() => {
    onCountChange?.(error !== null || isPending ? null : wishes.length);
  }, [error, isPending, onCountChange, wishes.length]);

  return (
    <>
      <Stack gap="md">
        {error ? (
          <ErrorState
            message={error.message}
            onRetry={() => {
              void refetch();
            }}
          />
        ) : isPending ? (
          <Stack gap="sm">
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Skeleton key={index} height={58} radius="sm" />
            ))}
          </Stack>
        ) : wishes.length === 0 ? (
          <EmptyState />
        ) : (
          <Stack gap="xs">
            {wishes.map((wish) => (
              <WishRow key={wish.id} wish={wish} onOpen={() => setSelectedWishId(wish.id)} />
            ))}
          </Stack>
        )}

        {!error && !isPending && wishes.length > 0 && (
          <WishlistTotal wishes={wishes} truncated={data?.hasMore ?? false} />
        )}
      </Stack>
      <WishDetails wishId={selectedWishId} onClose={() => setSelectedWishId(null)} />
    </>
  );
}

function WishRow({ wish, onOpen }: { wish: Wish; onOpen: () => void }) {
  return (
    <Card
      component="button"
      type="button"
      padding="sm"
      radius="sm"
      bg="var(--mantine-color-dark-7)"
      className="wishlist-row"
      onClick={onOpen}
      aria-label={`Открыть детали «${wish.title}»`}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <PriorityBadge priority={wish.priority} />
            <Text size="sm" fw={500} lineClamp={2}>
              {wish.title}
            </Text>
          </Group>
          {/* An icon rather than a preposition: the initiator is free-form text,
              and no Russian preposition fits every name someone may type in. */}
          <Group gap={4} wrap="nowrap" c="dimmed">
            <IconUser size={13} stroke={1.5} style={{ flexShrink: 0 }} />
            <Text size="xs" c="dimmed" lineClamp={1}>
              {wish.initiator}
            </Text>
          </Group>
        </Stack>

        <Group gap="xs" wrap="nowrap" style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(wish.price)}
          </Text>
          <IconChevronRight size={17} stroke={1.5} opacity={0.55} />
        </Group>
      </Group>
    </Card>
  );
}

/** Zero is the rank an unranked wish gets, so it is shown as exactly that. */
function PriorityBadge({ priority }: { priority: number }) {
  if (priority === 0) {
    return (
      <Badge color="gray" size="sm" style={{ flexShrink: 0 }}>
        —
      </Badge>
    );
  }

  return (
    <Tooltip label={`Приоритет: ${priority}`} withArrow>
      <Badge color="brand" size="sm" style={{ flexShrink: 0 }}>
        {priority}
      </Badge>
    </Tooltip>
  );
}

function WishlistTotal({ wishes, truncated }: { wishes: Wish[]; truncated: boolean }) {
  const total = wishes.reduce((sum, wish) => sum + wish.price, 0);

  return (
    <>
      <Divider />
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          {truncated ? `Первые ${wishes.length} из списка` : 'Итого'}
        </Text>
        <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(total)}
        </Text>
      </Group>
    </>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Center py={24}>
      <Stack gap="xs" align="center" maw={280}>
        <ThemeIcon variant="light" color="red" size="lg" radius="md">
          <IconAlertTriangle size={18} stroke={1.5} />
        </ThemeIcon>
        <Text size="sm" fw={600}>
          Не удалось загрузить хотелки
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          {message}
        </Text>
        <Button size="xs" variant="default" mt={4} onClick={onRetry}>
          Повторить
        </Button>
      </Stack>
    </Center>
  );
}

function EmptyState() {
  return (
    <Center py={32}>
      <Stack gap="xs" align="center" maw={240}>
        <IconSparkles size={26} stroke={1.5} opacity={0.5} />
        <Text size="sm" c="dimmed" ta="center">
          Пока ничего не хочется
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          Добавить покупку и выставить ей приоритет можно через MCP
        </Text>
      </Stack>
    </Center>
  );
}
